import jsPDF from "jspdf";
import uuid from "@/app/infrastructures/misc/common-library/Uuid";
import { AccountEntities } from "@/domain/entities/Account";
import { formatByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import moment from "moment";
import { CargoV2Data } from "@/domain/entities/CargoV2";


let doc: any = new jsPDF();

export const generatePti = async ({
  data,
  companyPlane: { logo, label },
  accountData
}: {
  data: CargoV2Data;
  companyPlane: { logo: string; label: string };
  accountData: AccountEntities;
}): Promise<void> => {
  const currentLocale = moment.locale();
  moment.locale("id");
  doc = new jsPDF("p", "mm", "a4", true);

  const margin = {
    left: 5,
    right: 5,
    top: 5,
    bottom: 5
  };

  const widthPaper = doc.internal.pageSize.getWidth();
  const centerStart = widthPaper / 2;
  const gapY = 7;
  const gapYSpacing = gapY * 1.5;
  const lineHeightFactor = 1.6;

  let y = margin.top;

  if (logo) {
    const logoProperties = doc.getImageProperties(logo);
    const logoAspectRatio = logoProperties.height / logoProperties.width;
    const logoWidth = 80;

    doc.addImage(
      logo,
      "PNG",
      centerStart - logoWidth / 2,
      margin.top,
      logoWidth,
      logoAspectRatio * logoWidth,
      uuid(),
      "FAST",
      0
    );

    y += logoAspectRatio * logoWidth;
  }

  doc.setFont("Montserrat", "bold");
  doc.setFontSize(16);
  const headerText = `PEMBERITAHUAN TENTANG ISI (PTI) LION PARCEL ${data.originCityCode}`;
  const headerTextWidth = doc.getTextWidth(headerText);
  doc.text(headerText, centerStart - headerTextWidth / 2, (y += 10));

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(11);
  doc.text(
    "Yang bertanda tangan di bawah ini :",
    margin.left,
    (y += gapYSpacing)
  );

  const semicolonSeparator = 85;
  const tabValueX = semicolonSeparator + 2;

  // nama pengirim
  doc.text("Nama pengirim", margin.left, (y += gapY));
  doc.text(`: Lion Parcel ${data.originCityCode}`, semicolonSeparator, y);

  // alamat pengirim
  const senderAddress = `${data.originCityCode} ${accountData.account_type_detail?.partnerLocation?.partner?.address}`;
  doc.text("Alamat", margin.left, (y += gapY));

  const senderAddressBox = widthPaper - tabValueX - margin.left - margin.right;
  const senderAddressLines = doc.splitTextToSize(
    senderAddress,
    senderAddressBox
  );
  doc.text(": ", semicolonSeparator, y);
  doc.text(senderAddressLines, tabValueX, y, {
    lineHeightFactor
  });

  const senderAddressLineHeight =
    (doc.getFontSize(senderAddressLines[0]) * lineHeightFactor) / 3;
  y += senderAddressLineHeight * senderAddressLines.length + 1.4;

  // nomor telepon pengirim
  doc.text("Nomor Telepon", margin.left, y);
  doc.text(
    `: ${accountData.account_type_detail.partnerLocation?.partner?.partner_phone_number}`,
    semicolonSeparator,
    y
  );

  // ktp pengirim
  doc.text("KTP / Identitas lainya", margin.left, (y += gapY));
  doc.text(": LION GROUP", semicolonSeparator, y);

  // deskripsi 2
  const description2 = `Menerangkan bahwa kiriman yang diserahkan untuk di angkut oleh ${label} yang di alamat kan kepada :`;
  const description2Box = widthPaper - margin.left - margin.right - 20;
  const description2Lines = doc.splitTextToSize(description2, description2Box);

  doc.setFont("Montserrat", "bold");
  doc.text(description2Lines, margin.left, (y += gapYSpacing), {
    lineHeightFactor
  });

  const description2LineHeight =
    (doc.getFontSize(description2Lines[0]) * lineHeightFactor) / 2;
  y += description2LineHeight * description2Lines.length;

  doc.setFont("Montserrat", "normal");

  // nama penerima
  doc.text("Nama penerima", margin.left, y);
  doc.text(`: Lion Parcel ${data.destinationCityCode}`, semicolonSeparator, y);

  // alamat penerima
  const receiverAddress = data.destinationCityCode;
  doc.text("Alamat", margin.left, (y += gapY));

  const receiverAddressBox =
    widthPaper - tabValueX - margin.left - margin.right;
  const receiverAddressLines = doc.splitTextToSize(
    receiverAddress,
    receiverAddressBox
  );
  doc.text(": ", semicolonSeparator, y);
  doc.text(receiverAddressLines, tabValueX, y, {
    lineHeightFactor
  });

  const receiverAddressLineHeight =
    (doc.getFontSize(receiverAddressLines[0]) * lineHeightFactor) / 3;
  y += receiverAddressLineHeight * receiverAddressLines.length + 1.4;

  // nomor telepon penerima
  doc.text("Nomor Telepon", margin.left, y);
  doc.text(":", semicolonSeparator, y);

  // AWB penerima
  doc.text("Dengan SURAT MUATAN UDARA No.", margin.left, (y += gapY));
  doc.text(`: ${data.cargoNumber}`, semicolonSeparator, y);

  // deskripsi isi barang
  doc.setFont("Montserrat", "bold");
  doc.text(
    "Berisi barang - barang sebagai berikut:",
    margin.left,
    (y += gapYSpacing)
  );

  const gapColumn = 10;
  const widthPerColumn =
    (widthPaper - margin.left - margin.right - gapColumn * 3) / 4;
  const totalWidthColumn = widthPerColumn + gapColumn;

  y += gapYSpacing;

  // JUMLAH KOLI
  const header1Text = "JUMLAH KOLI";
  const widthHeader1Text = doc.getTextWidth(header1Text);
  const widthBeforeHeader1Text = margin.left;
  const header1TextStartX =
    widthBeforeHeader1Text + (widthPerColumn / 2 - widthHeader1Text / 2);
  doc.text(header1Text, header1TextStartX, y);

  // SATUAN
  const header2Text = "SATUAN";
  const widthHeader2Text = doc.getTextWidth(header2Text);
  const widthBeforeHeader2Text = widthBeforeHeader1Text + totalWidthColumn;
  const header2TextStartX =
    widthBeforeHeader2Text + (widthPerColumn / 2 - widthHeader2Text / 2);
  doc.text(header2Text, header2TextStartX, y);

  // ISI BARANG
  const header3Text = "ISI BARANG";
  const widthHeader3Text = doc.getTextWidth(header3Text);
  const widthBeforeHeader3Text = widthBeforeHeader2Text + totalWidthColumn;
  const header3TextStartX =
    widthBeforeHeader3Text + (widthPerColumn / 2 - widthHeader3Text / 2);
  doc.text(header3Text, header3TextStartX, y);

  // BERAT KG
  const header4Text = "BERAT KG.";
  const widthHeader4Text = doc.getTextWidth(header4Text);
  const widthBeforeHeader4Text = widthBeforeHeader3Text + totalWidthColumn;
  const header4TextStartX =
    widthBeforeHeader4Text + (widthPerColumn / 2 - widthHeader4Text / 2);
  doc.text(header4Text, header4TextStartX, y);

  const goodsDetail = [
    {
      totalPieces: data.totalPieces,
      type: "KOLI",
      nog: data.nog,
      weight: data.totalActualCargoGrossWeight
    }
  ];

  for (const [i] of Array(6).entries()) {
    let detailData = { totalPieces: 0, type: "", nog: "", weight: 0 };

    // fill row ${i}
    y += gapY;

    // ISI - JUMLAH KOLI
    let startFillX = margin.left;
    let latestFillX = startFillX + widthPerColumn;

    if (i < goodsDetail.length) {
      detailData = goodsDetail[i];
      const totalPieces = String(detailData.totalPieces);
      const widthTotalPieces = doc.getTextWidth(totalPieces);
      const totalPiecesStartX =
        startFillX + (widthPerColumn / 2 - widthTotalPieces / 2);

      doc.setFont("Montserrat", "bold");
      doc.text(totalPieces, totalPiecesStartX, y);
    } else {
      doc.setDrawColor("#A7A8AA");
      doc.setLineDashPattern([0.5, 0.5], 0);
      doc.line(startFillX, y, latestFillX, y);
    }

    doc.setDrawColor("#EBECF0");
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([0, 0], 0);
    doc.line(startFillX, y + 2, latestFillX + gapColumn, y + 2);

    // ISI - SATUAN
    startFillX = latestFillX + gapColumn;
    latestFillX = startFillX + widthPerColumn;

    if (i < goodsDetail.length) {
      const type = detailData.type;
      const widthType = doc.getTextWidth(type);
      const typeStartX = startFillX + (widthPerColumn / 2 - widthType / 2);

      doc.setFont("Montserrat", "bold");
      doc.text(type, typeStartX, y);
    } else {
      doc.setDrawColor("#A7A8AA");
      doc.setLineDashPattern([0.5, 0.5], 0);
      doc.line(startFillX, y, latestFillX, y);
    }

    doc.setDrawColor("#EBECF0");
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([0, 0], 0);
    doc.line(margin.left, y + 2, latestFillX + gapColumn, y + 2);

    // ISI - ISI BARANG
    startFillX = latestFillX + gapColumn;
    latestFillX = startFillX + widthPerColumn;

    if (i < goodsDetail.length) {
      const nog = detailData.nog;
      const widthNog = doc.getTextWidth(nog);
      const nogStartX = startFillX + (widthPerColumn / 2 - widthNog / 2);

      doc.setFont("Montserrat", "bold");
      doc.text(nog, nogStartX, y);
    } else {
      doc.setDrawColor("#A7A8AA");
      doc.setLineDashPattern([0.5, 0.5], 0);
      doc.line(startFillX, y, latestFillX, y);
    }

    doc.setDrawColor("#EBECF0");
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([0, 0], 0);
    doc.line(startFillX, y + 2, latestFillX + gapColumn, y + 2);

    // ISI - BERAT KG
    startFillX = latestFillX + gapColumn;
    latestFillX = startFillX + widthPerColumn;

    if (i < goodsDetail.length) {
      const weight = String(detailData.weight);
      const widthWeight = doc.getTextWidth(weight);
      const weightStartX = startFillX + (widthPerColumn / 2 - widthWeight / 2);

      doc.setFont("Montserrat", "bold");
      doc.text(weight, weightStartX, y);
    } else {
      doc.setDrawColor("#A7A8AA");
      doc.setLineDashPattern([0.5, 0.5], 0);
      doc.line(startFillX, y, latestFillX, y);
    }

    doc.setDrawColor("#EBECF0");
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([0, 0], 0);
    doc.line(startFillX, y + 2, latestFillX + gapColumn, y + 2);
  }

  const totalWeight = "JUMLAH BERAT :";
  const totalWeightStartX = 140;

  doc.setFont("Montserrat", "normal");
  doc.text(totalWeight, totalWeightStartX, (y += gapYSpacing));

  const totalWeightValue = `${formatByCountry(
    parseByCountry(data.totalActualCargoGrossWeight),
    { type: "weight" }
  )} KG`;
  const widthTotalWeightValue = doc.getTextWidth(totalWeightValue);
  doc.setFont("Montserrat", "bold");
  doc.text(
    totalWeightValue,
    widthPaper - margin.right - widthTotalWeightValue,
    y
  );

  doc.setFont("Montserrat", "normal");
  const lastDescription =
    "Setelah dari itu pengirim menerangkan dan mengetahui bahwa pengisian pemberitahuan tentang isi. Dengan tidak benar dapat di hukum dengan denda dan / atau pensitaan atas kiriman itu. Pengirim menerangkan pula bahwa ia bersedia sewaktu-waktu membayar ganti rugi yang di sebabkan karena pemberitahuan tentang isi telah di isi tidak dengan sebenarnya.";
  const lastDescriptionBox = widthPaper - margin.left - margin.right;
  const lastDescriptionLines = doc.splitTextToSize(
    lastDescription,
    lastDescriptionBox
  );
  doc.text(lastDescriptionLines, margin.left, (y += gapYSpacing), {
    lineHeightFactor
  });

  const lastDescriptionLineHeight =
    (doc.getFontSize(lastDescriptionLines[0]) * lineHeightFactor) / 3;
  y += lastDescriptionLineHeight * lastDescriptionLines.length;

  const boxSignature = 80;
  const currentDate = moment().format("dddd, DD MMMM YYYY");
  const widthCurrentDate = doc.getTextWidth(currentDate);

  doc.text(
    currentDate,
    widthPaper - margin.right - boxSignature / 2 - widthCurrentDate / 2,
    (y += gapY)
  );

  const signAndName = "(Tanda Tangan & Nama Jelas Pengirim)";
  const widthSignAndName = doc.getTextWidth(signAndName);

  doc.text(
    signAndName,
    widthPaper - margin.right - boxSignature / 2 - widthSignAndName / 2,
    (y += gapY * 4)
  );

  doc.setProperties({
    title: `PEMBERITAHUAN TENTANG ISI (PTI) LION PARCEL ${data.originCityCode} - ${data.cargoNumber}`
  });

  doc.autoPrint();

  window.open(doc.output("bloburl"), "_blank");

  doc = new jsPDF("p", "mm", "a6", true);
  y = margin.top;

  moment.locale(currentLocale);
};
